/**
 * Data layer for the trainer dashboard.
 *
 * Written from scratch as a hook so the UI holds no fetching, no
 * derivation and no date arithmetic — it renders what it is given.
 * Everything the old component derived inline lives here as one memo,
 * computed once per payload rather than once per render.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '../../common/Toast/toast';

import TrainerService from '../../../../services/TrainerDashboardService';

/** Date#getDay() indexes this. */
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * `days` reaches us in two forms: the batch schema defaults to
 * "mon,tue,…" while the Add Batch form saves "monday,tuesday,…".
 * Three letters is unambiguous across all seven, so compare on that.
 */
export const dayKeys = (value) =>
  String(value || '')
    .split(/[,\s]+/)
    .map((d) => d.trim().toLowerCase().slice(0, 3))
    .filter((d) => DAY_KEYS.includes(d));

/** Calendar days apart, not elapsed milliseconds — 18:00 today is "today". */
export const daysUntil = (value) => {
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return '';
  const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((midnight(target) - midnight(new Date())) / 86400000);
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
};

export const formatDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatTime = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/** How far through its scheduled span a batch is. Timeline, not mastery. */
export const timelineProgress = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  const now = new Date();
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 0;
  if (now <= s) return 0;
  if (now >= e) return 100;
  return Math.round(((now - s) / (e - s)) * 100);
};

export const EMPTY_ATTENDANCE_FILTERS = {
  batchId: '',
  courseName: '',
  mode: '',
  batchStatus: 'active',
  band: '',
  tail: '',
  minRate: '',
  maxRate: '',
  dateFrom: '',
  dateTo: '',
  search: '',
  sort: 'rate-asc',
};

export function useTrainerDashboard(trainerId) {
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!trainerId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    TrainerService.getTrainerDashboardData(trainerId)
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch((err) => {
        console.error('Trainer dashboard load failed:', err);
        if (!cancelled) setError('Failed to load dashboard data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trainerId]);

  const trainer = payload?.trainer ?? null;
  const stats = payload?.stats ?? null;
  const insights = payload?.insights ?? null;
  const batches = useMemo(
    () => (Array.isArray(payload?.batches) ? payload.batches : []),
    [payload]
  );

  /* ── Everything derived from the batch list, computed once ── */
  const view = useMemo(() => {
    const now = new Date();
    const todayKey = DAY_KEYS[now.getDay()];

    const active = [];
    const upcoming = [];
    const completed = [];
    const modes = {};
    const courses = new Set();
    const classrooms = new Set();
    let activeStudents = 0;

    batches.forEach((batch) => {
      const start = new Date(batch.startTime);
      const end = new Date(batch.endTime);
      if (start > now) upcoming.push(batch);
      else if (end < now) completed.push(batch);
      else {
        active.push(batch);
        activeStudents += Number(batch.studentCount) || 0;
      }

      const mode = String(batch.mode || '').trim();
      if (mode) modes[mode] = (modes[mode] || 0) + 1;
      if (batch.courseName && batch.courseName !== 'Unknown Course') courses.add(batch.courseName);
      if (batch.classroom) classrooms.add(batch.classroom);
    });

    // A batch meets today when it is running AND its weekly pattern
    // names today. An empty pattern means every day.
    const todayClasses = active
      .filter((b) => {
        const days = dayKeys(b.days);
        return days.length === 0 || days.includes(todayKey);
      })
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    /**
     * Running batches actually in trouble, with the reason attached.
     * Three independent checks; any one lists the batch.
     */
    const stat = insights?.batchStats || {};
    const attention = [];

    active.forEach((batch) => {
      const s = stat[String(batch.id)];
      if (!s) return;
      const reasons = [];

      // Only judge attendance once a session has been marked — a batch
      // that has not met yet is not a batch with bad attendance.
      if (s.attendanceRate !== null && s.sessions > 0 && s.attendanceRate < 50) {
        reasons.push({
          tone: 'bad',
          label: `${s.attendanceRate}% attendance`,
          detail: `${s.presentMarks} present of ${s.expectedMarks} expected across ${s.sessions} session${s.sessions === 1 ? '' : 's'}`,
        });
      }

      // null when the course has no syllabus loaded, so an unmapped
      // course is never reported as behind on no evidence.
      if (s.scheduleGap !== null && s.scheduleGap >= 25) {
        reasons.push({
          tone: 'warn',
          label: `${s.scheduleGap}% behind syllabus`,
          detail: `${s.elapsedProgress}% of the dates gone, ${s.topicsCovered} of ${s.topicsTotal} topics taught`,
        });
      }

      if (s.daysRemaining !== null && s.daysRemaining <= 14 && s.syllabusProgress !== null && s.syllabusProgress < 80) {
        reasons.push({
          tone: 'bad',
          label: s.daysRemaining <= 0 ? 'Ends today' : `${s.daysRemaining} day${s.daysRemaining === 1 ? '' : 's'} left`,
          detail: `${s.topicsRemaining} topic${s.topicsRemaining === 1 ? '' : 's'} still to cover`,
        });
      }

      if (reasons.length) {
        attention.push({
          batch,
          stats: s,
          reasons,
          // Worst first: failing all three outranks failing one, and
          // within that the lower attendance leads.
          severity: reasons.length * 1000 + (100 - (s.attendanceRate ?? 100)) + Math.max(0, s.scheduleGap ?? 0),
        });
      }
    });

    attention.sort((a, b) => b.severity - a.severity);

    return {
      active,
      upcoming,
      completed,
      todayClasses,
      attention,
      activeStudents,
      modes,
      courseCount: courses.size,
      classroomCount: classrooms.size,
    };
  }, [batches, insights]);

  return { trainer, stats, insights, batches, view, loading, error };
}

/** Roster-level attendance. The API owns filtering, sorting and paging. */
export function useAttendanceRoster(trainerId, active) {
  const [filters, setFilters] = useState(EMPTY_ATTENDANCE_FILTERS);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [options, setOptions] = useState({ batches: [], courses: [], modes: [] });
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (wanted) => {
      if (!trainerId) return;
      setLoading(true);
      try {
        const res = await TrainerService.getAttendanceRoster({
          ...filters,
          trainerId,
          page: wanted,
          limit: pagination.limit,
        });
        setRows(res.rows);
        setSummary(res.summary);
        // Options describe every batch the trainer owns, so a filter
        // that matches nothing must not empty the dropdowns.
        if (res.filterOptions?.batches?.length) setOptions(res.filterOptions);
        setPagination((p) => ({ ...p, ...(res.pagination || {}), page: wanted }));
      } catch (err) {
        console.error('Attendance roster load failed:', err);
        toast.error('Failed to load attendance');
        setRows([]);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    },
    [trainerId, filters, pagination.limit]
  );

  // Debounced so typing in the search box is one request, not one per key.
  useEffect(() => {
    if (!active || !trainerId) return undefined;
    const id = setTimeout(() => {
      setPage(1);
      load(1);
    }, 350);
    return () => clearTimeout(id);
  }, [active, trainerId, load]);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const activeFilterCount = Object.entries(filters).filter(
    ([k, v]) => v !== '' && v !== EMPTY_ATTENDANCE_FILTERS[k]
  ).length;

  const goToPage = useCallback(
    (next) => {
      setPage(next);
      load(next);
    },
    [load]
  );

  return {
    filters,
    setFilter,
    resetFilters: () => setFilters(EMPTY_ATTENDANCE_FILTERS),
    activeFilterCount,
    rows,
    summary,
    options,
    page,
    pagination,
    loading,
    goToPage,
  };
}
